import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import universityService, { University } from '../services/universityService';
import { toast } from 'react-toastify';

const UniversitiesPage: React.FC = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState<University | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    emailDomain: '',
    contactEmail: '',
    phoneNumber: '',
    address: ''
  });

  useEffect(() => {
    // Redirect if not superadmin
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      toast.error('Access denied. Super administrator privileges required.');
      return;
    }
    loadUniversities();
  }, [isSuperAdmin, navigate]);

  const loadUniversities = async () => {
    try {
      setLoading(true);
      const data = await universityService.getUniversities();
      setUniversities(data);
    } catch (error) {
      console.error('Failed to load universities:', error);
      toast.error('Failed to load universities');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleOpenDialog = (university?: University) => {
    if (university) {
      setEditingUniversity(university);
      setFormData({
        name: university.name,
        emailDomain: university.emailDomain || '',
        contactEmail: university.contactEmail || '',
        phoneNumber: university.phoneNumber || '',
        address: university.address || ''
      });
    } else {
      setEditingUniversity(null);
      setFormData({
        name: '',
        emailDomain: '',
        contactEmail: '',
        phoneNumber: '',
        address: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUniversity(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('University name is required');
        return;
      }

      const universityData = {
        ...formData,
        status: 'active'
      };

      if (editingUniversity) {
        await universityService.updateUniversity(editingUniversity.id, universityData);
        toast.success('University updated successfully');
      } else {
        await universityService.createUniversity(universityData);
        toast.success('University created successfully');
      }

      handleCloseDialog();
      loadUniversities();
    } catch (error) {
      console.error('Failed to save university:', error);
      toast.error('Failed to save university');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this university? This action cannot be undone.')) {
      try {
        await universityService.deleteUniversity(id);
        toast.success('University deleted successfully');
        loadUniversities();
      } catch (error) {
        console.error('Failed to delete university:', error);
        toast.error('Failed to delete university. It may have associated data.');
      }
    }
  };

  if (!isSuperAdmin()) {
    return null;
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - University Management
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" onClick={handleBack}>
              ← Back to Dashboard
            </Button>
            <Typography variant="h4">
              University Management
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => handleOpenDialog()}>
            Add New University
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          As a super administrator, you can manage all universities in the system. Regular administrators can only manage their own university settings.
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading universities...</Typography>
          </Box>
        ) : (
          <Card>
            <CardContent>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email Domain</TableCell>
                      <TableCell>Contact Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Students</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {universities.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No universities found
                        </TableCell>
                      </TableRow>
                    ) : (
                      universities.map((university) => (
                        <TableRow key={university.id}>
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {university.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{university.emailDomain || '-'}</TableCell>
                          <TableCell>{university.contactEmail || '-'}</TableCell>
                          <TableCell>{university.phoneNumber || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={university.status}
                              color={university.status === 'active' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{university.studentCount || 0}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(university)}
                                title="Edit"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(university.id)}
                                title="Delete"
                                disabled={university.studentCount > 0}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingUniversity ? 'Edit University' : 'Add New University'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="University Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Email Domain"
                value={formData.emailDomain}
                onChange={(e) => setFormData({ ...formData, emailDomain: e.target.value })}
                placeholder="example.edu"
                helperText="Students must use this email domain to register"
              />
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              />
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {editingUniversity ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default UniversitiesPage;